import { LoadingFieldSkeleton } from 'components/secondaryMarket/shared/FieldSkeleton';
import type { TagItem } from 'components/shared/Tags/SgTagsPicker';
import { SgTagsPicker } from 'components/shared/Tags/SgTagsPicker';
import { uniqBy } from 'lodash-es';
import { FC, useCallback, useMemo } from 'react';
import type { CpOrderSettlementType } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import { toSettlementTypeKey } from 'state/secondaryMarket/shared/utils';

import { FeedbackMessage } from '../FeedbackMessage';

type SettlementTypeSelectorProps = {
  availableSettlementTypes: ReadonlyArray<CpOrderSettlementType> | undefined;
  settlementType: string | undefined;
  isFrozen: boolean;
  isLoadingSettlement: boolean;

  onSettlementTypeChanged: (value: string | undefined) => void;
};

export const SettlementTypeSelector: FC<SettlementTypeSelectorProps> = ({
  settlementType,
  availableSettlementTypes,
  onSettlementTypeChanged,
  isLoadingSettlement,
  isFrozen,
}) => {
  const onSettlementTypeSelected = useCallback(
    (selection: readonly string[]) => {
      if (selection?.length === 1) {
        onSettlementTypeChanged(selection[0]);
      }
    },
    [onSettlementTypeChanged],
  );

  const available = useMemo(
    () =>
      uniqBy(
        availableSettlementTypes?.map(
          item =>
            ({
              key: toSettlementTypeKey(item)!,
              label: `${item.settlementType} - ${item.marketType} (${item.description})`,
            }) as TagItem,
        ),
        'key',
      ) ?? [],
    [availableSettlementTypes],
  );

  const singleSelection: string[] = useMemo(() => {
    if (settlementType) {
      return availableSettlementTypes?.map(x => toSettlementTypeKey(x)!)?.filter(x => x === settlementType) ?? [];
    }
    return [];
  }, [settlementType, availableSettlementTypes]);

  const hasSelectedMissingSettlementType = !!settlementType && singleSelection.length === 0;

  const missing: TagItem = {
    key: settlementType!,
    label: `${settlementType}`,
    forced: true,
  };

  return (
    <>
      {isLoadingSettlement ? (
        <LoadingFieldSkeleton />
      ) : (
        <>
          <SgTagsPicker
            id="SettlementType"
            onSelectionKeysChanged={onSettlementTypeSelected}
            singleSelect
            noIcon
            selection={hasSelectedMissingSettlementType ? [missing.key] : singleSelection}
            available={hasSelectedMissingSettlementType ? [...available, missing] : available}
            disabled={isFrozen}
            maxDisplayedItems={available.length}
            noClear
            hasTooltip
            placeholder="Settlement type"
            e2e="settlement-type"
          />
          {hasSelectedMissingSettlementType && (
            <FeedbackMessage labelKey="secondaryMarket.errors.settlementType.incoherent" color="info" />
          )}
        </>
      )}
    </>
  );
};
